import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useSelector, useDispatch } from 'react-redux'
import { cond } from 'lodash-es'
import Popper from '@mui/material/Popper'
import Grow from '@mui/material/Grow'
import Box from '@mui/material/Box'
import ContentWrapper from 'components/common/ContentWrapper'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import CloseIcon from '@mui/icons-material/Close'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import styled from '@emotion/styled'
import { GtmAgent } from 'utils/hooks/analytics'

import {
  selectArticleSearchRecords,
  selectAlbumSearchRecords,
  selectVenderSearchRecords,
  addSearchRecord,
  removeAllSearchRecord,
} from 'store/cache/cacheSlice'
import { selectWebview } from 'store/webview/webviewSlice'
import { useKeywords, usePhotoTags } from 'utils/api/hooks'
import { headerHeight } from 'config/theme'
import { SearchSVG, SearchBoldSVG } from 'components/common/SVGElements'
import Link from 'components/common/A'
import Select from 'components/common/Select'
import { FunctionModal } from 'components/common/Modal'

const IconWrapper = styled.span`
  display: inline-flex;
  cursor: pointer;
`

const SearchWrapper = styled.div`
  ${({ theme, $isLsApp }) => `
    position: ${$isLsApp ? 'fixed' : 'initial'};
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    border: 1px solid ${theme.palette.grey.secondary};
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    border-radius: ${$isLsApp ? 0 : '24px'};
    z-index: 9999;
    display: flex;
    flex-direction: column;
    max-width: 1600px;
    margin: auto;

    ${theme.breakpoints.up('lg')} {
      bottom: auto;
      margin: 0 36px;
    }
    ${theme.breakpoints.up('xl')} {
      margin: 0 48px;
    }
    ${theme.breakpoints.up('xxl')} {
      margin: 0 auto;
    }
  `}
`

const InputPaper = styled(Paper)`
  ${({ theme }) => `
    padding: 0 10px 0 20px;
    border-radius: 32px;
    display: flex;
    align-items: center;
    flex: 1;
    caret-color: ${theme.palette.primary.main};
  `}
`

const SearchInput = styled(InputBase)`
  flex: 1;
  padding: 14px 0;
  font-size: 16px;
  ${({ theme }) => `
    margin-left: ${theme.spacing(2)};
  `}

  > .MuiInputBase-input {
    padding: 0;
  }
`
const SelectButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SelectButton = styled(Box)`
  ${({ theme, $selected }) => `
    background: ${
      $selected ? theme.palette.primary.main : theme.palette.grey.third
    };
    color: ${$selected ? '#FFFFFF' : ''};
    border-radius: 25px;
    margin-left: 8px;
    cursor: pointer;
    &:first-child {
      margin-left: 24px;
    }
    &:hover {
      background: ${
        $selected ? theme.palette.primary.main : theme.palette.primary.bgPlus1
      };
    }
  `}
`
const ItemButton = styled(Button)`
  ${({ theme }) => `
    &, &:hover {
      font-weight: initial;
      color: ${theme.palette.text.minus2};
      padding: ${theme.spacing(2.5)} ${theme.spacing(4)};
      display: flex;
      align-items: center;
      justify-content: flex-start;
      background: ${theme.palette.grey.third};
      border-radius: 25px;
    }
    &:hover {
      background-color: ${theme.palette.primary.bgPlus1} !important;
    }
  `}
`

const genSearchRoute = (keyword, type = 'article') => {
  if (type === 'article') {
    return `/article/search?keyword=${encodeURIComponent(keyword)}`
  } else if (type === 'album') {
    return `/album/search?keyword=${encodeURIComponent(keyword)}`
  } else if (type === 'vender') {
    return `/vender/search?keyword=${encodeURIComponent(keyword)}`
  }
  return '/'
}

const SearchContent = ({ webview, isSmall, isLarge, onClose }) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const [keyIn, setKeyIn] = useState('')
  const [keyInSearchType, setKeyInSearchType] = useState('vender')
  const onSearchKeyPress = (event) => {
    if ((event.charCode === 13 || event.key === 'Enter') && !!keyIn) {
      GtmAgent.directSendToGTM({
        event: 'living_action',
        living_event_name: 'search_send',
        living_search_type: {
          vender: '廠商',
          article: '文章',
          album: '圖片',
        }[keyInSearchType],
        living_search_keyword: keyIn,
        living_search_location: 'header',
      })

      dispatch(addSearchRecord({ keyword: keyIn, type: keyInSearchType }))
      router.push(genSearchRoute(keyIn, keyInSearchType))
      if (onClose) {
        onClose(event)
      }
    }
  }

  const { data: keywords } = useKeywords(() =>
    keyInSearchType === 'article' ? { limit: 10 } : null
  )
  const { data: photoTags } = usePhotoTags(() =>
    keyInSearchType === 'album' ? { limit: 10 } : null
  )

  const articleSearchRecords = useSelector(selectArticleSearchRecords)
  const albumSearchRecords = useSelector(selectAlbumSearchRecords)
  const venderSearchRecords = useSelector(selectVenderSearchRecords)
  const searchRecords = cond([
    [() => keyInSearchType === 'article', () => articleSearchRecords],
    [() => keyInSearchType === 'album', () => albumSearchRecords],
    [() => keyInSearchType === 'vender', () => venderSearchRecords],
    [() => true, () => articleSearchRecords],
  ])()

  return (
    <SearchWrapper $isLsApp={webview?.name === 'lsApp' || isSmall}>
      <ContentWrapper>
        {isSmall ? (
          <Box display="flex" alignItems="center" py={4} position="relative">
            <IconWrapper style={{ position: 'absolute' }}>
              <CloseIcon ptr onClick={onClose} />
            </IconWrapper>
            <Box fontSize="17px" fontWeight="fontWeightBold" margin="auto">
              搜尋
            </Box>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box fontSize="24px" fontWeight="600" mt={6} mb={2}>
              搜尋
            </Box>
            <Box ml={7.5} mt={4}>
              <IconWrapper>
                <CloseIcon ptr onClick={onClose} />
              </IconWrapper>
            </Box>
          </Box>
        )}
      </ContentWrapper>
      <ContentWrapper display="flex" alignItems="center" mt={4} px={{ md: 8 }}>
        <InputPaper variant="outlined">
          <SearchSVG />
          <SearchInput
            placeholder={
              '搜尋' +
              {
                article: '居家文章',
                album: '居家圖片',
                vender: '服務廠商',
              }[keyInSearchType]
            }
            id="header-search-input"
            value={keyIn}
            onChange={(event) => setKeyIn(event.target.value)}
            onKeyPress={onSearchKeyPress}
            inputProps={{ inputMode: 'search' }}
          />

          {isSmall ? (
            <>
              <IconWrapper>
                <img
                  src="/img/ic_input_clean_mobile.svg"
                  style={{ marginRight: '24px' }}
                  onClick={() => {
                    setKeyIn('')
                  }}
                />
              </IconWrapper>
              <Select
                value={keyInSearchType}
                variant="standard"
                $size="large"
                style={{
                  background: '#F6F6F6',
                  borderRadius: '25px',
                  padding: '3px 16px',
                }}
                color="textPrimary"
                iconColor="textPrimary"
                onChange={(e) => setKeyInSearchType(e.target.value)}
              >
                <option value="vender">廠商</option>
                <option value="article">文章</option>
                <option value="album">圖片</option>
              </Select>
            </>
          ) : (
            <SelectButtonContainer>
              <SelectButton
                py={2.5}
                px={4}
                onClick={() => setKeyInSearchType('vender')}
                $selected={keyInSearchType === 'vender'}
              >
                廠商
              </SelectButton>
              <SelectButton
                py={2.5}
                px={4}
                onClick={() => setKeyInSearchType('article')}
                $selected={keyInSearchType === 'article'}
              >
                文章
              </SelectButton>
              <SelectButton
                py={2.5}
                px={4}
                onClick={() => setKeyInSearchType('album')}
                $selected={keyInSearchType === 'album'}
              >
                圖片
              </SelectButton>
            </SelectButtonContainer>
          )}
        </InputPaper>
      </ContentWrapper>
      <ContentWrapper overflow="auto" px={{ md: 8 }}>
        {!!searchRecords?.length && (
          <>
            <Box mt={6} mb={3} display="flex">
              <Box flexGrow={isLarge ? 0 : 1} mr={3}>
                <Typography color="text.disabled">搜尋紀錄</Typography>
              </Box>
              <Box fontSize="medium">
                <Link
                  href=""
                  $color="primary.main"
                  onClick={() => dispatch(removeAllSearchRecord())}
                >
                  清除
                </Link>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {searchRecords?.map((record, index) => (
                <Grid item key={`record-${index}`}>
                  <span onClick={(event) => onClose && onClose(event)}>
                    <Link
                      href={genSearchRoute(record.keyword, record.type)}
                      $origin
                    >
                      <ItemButton fullWidth>
                        <SearchBoldSVG width={12} height={12} />
                        <Box ml={3}>{record.keyword}</Box>
                      </ItemButton>
                    </Link>
                  </span>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {!!keywords?.length && (
          <>
            <Box mt={6} mb={3}>
              <Typography color="text.disabled">熱門文章標籤</Typography>
            </Box>

            <Grid container spacing={3}>
              {keywords.map((keyword, index) => (
                <Grid item key={`keyword-${index}`}>
                  <span onClick={(event) => onClose && onClose(event)}>
                    <Link href={`/article/tag/${keyword.urlId}`} $origin>
                      <ItemButton fullWidth>
                        <SearchBoldSVG width={12} height={12} />
                        <Box ml={3}>{keyword.name}</Box>
                      </ItemButton>
                    </Link>
                  </span>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {!!photoTags?.length && (
          <>
            <Box mt={6} mb={3}>
              <Typography color="text.disabled">熱門標籤</Typography>
            </Box>

            <Grid container spacing={3}>
              {photoTags.map((photoTag, index) => (
                <Grid item key={`tag-${index}`}>
                  <span onClick={(event) => onClose && onClose(event)}>
                    <Link href={`/album?tag=${photoTag.urlId}`} $origin>
                      <ItemButton fullWidth>
                        <SearchBoldSVG width={12} height={12} />
                        <Box ml={3}>{photoTag.name}</Box>
                      </ItemButton>
                    </Link>
                  </span>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        <Box mt={{ xs: 16, lg: 9 }} />
      </ContentWrapper>
    </SearchWrapper>
  )
}

const SearchPanel = ({ onClose, open }) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'))
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))

  const webview = useSelector(selectWebview)

  useEffect(() => {
    setTimeout(() => {
      if (open && document.getElementById('header-search-input')) {
        document.getElementById('header-search-input').focus()
      }
    }, 500)
  }, [open])

  if (isSmall) {
    return (
      <FunctionModal open={open} onClose={onClose}>
        <SearchContent
          webview={webview}
          isSmall={isSmall}
          isLarge={isLarge}
          onClose={onClose}
        />
      </FunctionModal>
    )
  } else {
    return (
      <ClickAwayListener onClickAway={onClose}>
        <Popper
          open={open}
          transition
          disablePortal
          style={{
            zIndex: 9999,
            top: webview?.name === 'lsApp' || isSmall ? 0 : headerHeight,
            right: 0,
            left: 0,
          }}
        >
          {({ TransitionProps, placement }) => (
            <Grow {...TransitionProps}>
              <div>
                <SearchContent
                  webview={webview}
                  isSmall={isSmall}
                  isLarge={isLarge}
                  onClose={onClose}
                />
              </div>
            </Grow>
          )}
        </Popper>
      </ClickAwayListener>
    )
  }
}

const PopupSearchPanel = ({ onClose, open }) => {
  return <SearchPanel onClose={onClose} open={open} />
}

export default PopupSearchPanel
